// Data layer for the PB Intelligence Layer. All Supabase reads for the
// /intelligence page live here so the page components stay presentational.
import { supabase } from '../services/supabase';
import { ActivityRow, DateRange, previousRange, AlertRule, AlertTrigger, ReportNotes } from './intelligence';

// ── Shared lightweight types ─────────────────────────────────────────────────
export interface FirmLite {
  id: string;
  name: string;
  type: string | null;
  role: 'Manager' | 'Allocator' | 'Firm';
  headquarters: string | null;
  aum_min: number | null;
  aum_max: number | null;
  aum_exact: number | null;
  website: string | null;
  created_at: string | null;
  owner_id: string | null;
}

export interface PersonLite {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
  firmId: string | null;
  firmName: string | null;
}

export interface ConnLite {
  id: string;
  allocator_firm_id: string | null;
  manager_firm_id: string | null;
  requested_by: string | null;
  status: string;
  pipeline_stage: string | null;
  created_at: string;
}

export interface FirmStat {
  firm: FirmLite;
  actionsInPeriod: number;
  prevActions: number;
  lastActive: string | null;
}

// Thrown-error helper: true when a relation is missing (migration not run yet).
export function isMissingTable(err: any): boolean {
  if (!err) return false;
  const s = `${err.code || ''} ${err.message || ''}`.toLowerCase();
  return err.code === '42p01' || s.includes('does not exist') || s.includes('could not find the table');
}

// ── Firms + roles ────────────────────────────────────────────────────────────
function deriveRole(type: string | null, managerSet: Set<string>, allocatorSet: Set<string>, id: string): 'Manager' | 'Allocator' | 'Firm' {
  const inM = managerSet.has(id), inA = allocatorSet.has(id);
  if (inM && !inA) return 'Manager';
  if (inA && !inM) return 'Allocator';
  const t = (type || '').toLowerCase();
  if (t.includes('alloc') || t.includes('lp') || t.includes('investor') || t.includes('family') ||
      t.includes('endowment') || t.includes('pension') || t.includes('foundation') || t.includes('fund_of')) return 'Allocator';
  if (t) return 'Manager';
  return 'Firm';
}

export async function loadFirms(): Promise<FirmLite[]> {
  const [{ data: firms }, { data: conns }] = await Promise.all([
    supabase.from('firms').select('id, name, type, headquarters, aum_min, aum_max, aum_exact, website, created_at, owner_id').order('name'),
    supabase.from('connections').select('allocator_firm_id, manager_firm_id'),
  ]);
  const managerSet = new Set<string>();
  const allocatorSet = new Set<string>();
  (conns || []).forEach((c: any) => {
    if (c.manager_firm_id) managerSet.add(c.manager_firm_id);
    if (c.allocator_firm_id) allocatorSet.add(c.allocator_firm_id);
  });
  return (firms || []).map((f: any) => ({
    id: f.id, name: f.name, type: f.type, headquarters: f.headquarters,
    aum_min: f.aum_min, aum_max: f.aum_max, aum_exact: f.aum_exact, website: f.website,
    created_at: f.created_at ?? null, owner_id: f.owner_id ?? null,
    role: deriveRole(f.type, managerSet, allocatorSet, f.id),
  }));
}

// ── People + firm membership ─────────────────────────────────────────────────
export async function loadPeople(firms?: FirmLite[]): Promise<PersonLite[]> {
  const firmList = firms || await loadFirms();
  const firmName = new Map(firmList.map(f => [f.id, f.name]));
  const firmOf = new Map<string, string>();
  // Owner membership first.
  firmList.forEach(f => { if (f.owner_id) firmOf.set(f.owner_id, f.id); });
  // firm_members overrides/extends ownership.
  const { data: members } = await supabase.from('firm_members').select('firm_id, user_id').is('removed_at', null);
  (members || []).forEach((m: any) => { if (m.user_id && m.firm_id) firmOf.set(m.user_id, m.firm_id); });

  const { data: profiles } = await supabase
    .from('profiles').select('id, full_name, email, role').eq('account_status', 'approved').order('full_name');
  return (profiles || []).map((p: any) => {
    const fid = firmOf.get(p.id) || null;
    return { id: p.id, full_name: p.full_name, email: p.email, role: p.role, firmId: fid, firmName: fid ? (firmName.get(fid) || null) : null };
  });
}

// membersOf map firmId → profileIds (for firm-scoped activity queries).
export function membersByFirm(people: PersonLite[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  people.forEach(p => { if (p.firmId) { const arr = m.get(p.firmId) || []; arr.push(p.id); m.set(p.firmId, arr); } });
  return m;
}

// ── Activity ─────────────────────────────────────────────────────────────────
export interface ActivityScope { firmId?: string | null; profileId?: string | null; profileIds?: string[] | null; limit?: number; }

export async function loadActivity(range: DateRange, scope: ActivityScope = {}): Promise<ActivityRow[]> {
  let q = supabase.from('activity_log').select('*')
    .gte('created_at', range.start.toISOString())
    .lte('created_at', range.end.toISOString())
    .order('created_at', { ascending: false });
  if (scope.firmId) q = q.eq('firm_id', scope.firmId);
  if (scope.profileId) q = q.eq('profile_id', scope.profileId);
  if (scope.profileIds && scope.profileIds.length) q = q.in('profile_id', scope.profileIds);
  if (scope.limit) q = q.limit(scope.limit);
  const { data, error } = await q;
  if (error) throw error;
  return (data as ActivityRow[]) || [];
}

// Count activity_log rows in a range, optionally filtered by action_type list.
export async function countActivity(range: DateRange, actionTypes?: string[]): Promise<number> {
  let q = supabase.from('activity_log').select('*', { count: 'exact', head: true })
    .gte('created_at', range.start.toISOString()).lte('created_at', range.end.toISOString());
  if (actionTypes && actionTypes.length) q = q.in('action_type', actionTypes);
  const { count, error } = await q;
  if (error) throw error;
  return count || 0;
}

// Generic count of a source table within a date range on a given date column.
async function countRange(table: string, dateCol: string, range: DateRange, apply?: (q: any) => any): Promise<number> {
  let q = supabase.from(table).select('*', { count: 'exact', head: true })
    .gte(dateCol, range.start.toISOString()).lte(dateCol, range.end.toISOString());
  if (apply) q = apply(q);
  const { count } = await q;
  return count || 0;
}

// ── Overview aggregates ──────────────────────────────────────────────────────
export interface OverviewMetrics {
  activeFirms: number;
  connections: number;
  eventIntros: number;
  aiQueries: number;
  messages: number;
  ddqs: number;
  docsAccessed: number;
  surveys: number;
}

export async function loadOverviewMetrics(range: DateRange): Promise<OverviewMetrics> {
  // Active firms: distinct firm_id present in activity_log within range.
  const { data: fa } = await supabase.from('activity_log').select('firm_id')
    .gte('created_at', range.start.toISOString()).lte('created_at', range.end.toISOString()).not('firm_id', 'is', null);
  const activeFirms = new Set((fa || []).map((r: any) => r.firm_id)).size;

  const [connections, aiQueries, messages, eventIntros, ddqs, docsAccessed, surveys] = await Promise.all([
    countActivity(range, ['connection_requested']),
    countActivity(range, ['ai_query_made']),
    countActivity(range, ['message_sent']),
    countRange('event_investor_schedule', 'created_at', range),
    countRange('ddqs', 'updated_at', range, q => q.eq('status', 'submitted')),
    countRange('data_room_access_log', 'accessed_at', range),
    countRange('pulse_responses', 'completed_at', range, q => q.eq('is_complete', true)),
  ]);
  return { activeFirms, connections, eventIntros, aiQueries, messages, ddqs, docsAccessed, surveys };
}

// Per-firm activity stats for leaderboard + at-risk.
export async function loadFirmStats(range: DateRange, firms: FirmLite[]): Promise<{ stats: FirmStat[]; lastActive: Map<string, string> }> {
  const prev = previousRange(range);
  const [{ data: inP }, { data: inPrev }, { data: allA }] = await Promise.all([
    supabase.from('activity_log').select('firm_id')
      .gte('created_at', range.start.toISOString()).lte('created_at', range.end.toISOString()).not('firm_id', 'is', null),
    supabase.from('activity_log').select('firm_id')
      .gte('created_at', prev.start.toISOString()).lte('created_at', prev.end.toISOString()).not('firm_id', 'is', null),
    supabase.from('activity_log').select('firm_id, created_at').not('firm_id', 'is', null).order('created_at', { ascending: false }).limit(8000),
  ]);
  const countMap = (rows: any[] | null) => {
    const m = new Map<string, number>();
    (rows || []).forEach(r => m.set(r.firm_id, (m.get(r.firm_id) || 0) + 1));
    return m;
  };
  const cur = countMap(inP), pr = countMap(inPrev);
  const lastActive = new Map<string, string>();
  (allA || []).forEach((r: any) => { if (!lastActive.has(r.firm_id)) lastActive.set(r.firm_id, r.created_at); });
  const stats: FirmStat[] = firms.map(f => ({
    firm: f, actionsInPeriod: cur.get(f.id) || 0, prevActions: pr.get(f.id) || 0, lastActive: lastActive.get(f.id) || null,
  }));
  return { stats, lastActive };
}

// ── Firm / individual product-card enrichment ────────────────────────────────
// Counts by action_type over an already-loaded activity array.
export function countByAction(rows: ActivityRow[]): Record<string, number> {
  const m: Record<string, number> = {};
  rows.forEach(r => { m[r.action_type] = (m[r.action_type] || 0) + 1; });
  return m;
}

export interface FirmProductDetail {
  // AltConnect
  pipelineStages: { stage: string; count: number }[];
  invested: number;
  connectionsApproved: number;
  // Talent
  interviews: number;
  hires: number;
  jobsPosted: number;
  candidatesConnected: number;
  // Service
  serviceCategories: string[];
  serviceConnections: number;
  // DDQ / data room
  ddqSubmitted: number;
  docsUploaded: number;
  docsAccessedByAllocators: number;
  uniqueAllocatorsAccessed: number;
  // Pulse
  surveysAssigned: number;
  surveysCompleted: number;
  lastSurveyAt: string | null;
  // Events
  eventsRegistered: number;
  eventMeetings: number;
}

// Source-table enrichment for a firm (accurate even for pre-instrumentation history).
export async function loadFirmProductDetail(firmId: string, memberIds: string[], range: DateRange): Promise<FirmProductDetail> {
  const inRange = (col: string, q: any) => q.gte(col, range.start.toISOString()).lte(col, range.end.toISOString());

  // Connections where this firm is the manager or allocator.
  const { data: conns } = await supabase.from('connections')
    .select('id, status, pipeline_stage, allocator_firm_id, manager_firm_id, created_at')
    .or(`allocator_firm_id.eq.${firmId},manager_firm_id.eq.${firmId}`);
  const connIds = (conns || []).map((c: any) => c.id);
  const connectionsApproved = (conns || []).filter((c: any) => c.status === 'approved').length;

  // Pipeline stages reached (latest stage per connection, in range by stage-change date).
  let pipelineStages: { stage: string; count: number }[] = [];
  let invested = 0;
  if (connIds.length) {
    const { data: stages } = await supabase.from('pipeline_stages').select('connection_id, stage, created_at').in('connection_id', connIds);
    const counts: Record<string, number> = {};
    (stages || []).forEach((s: any) => { counts[s.stage] = (counts[s.stage] || 0) + 1; });
    pipelineStages = Object.entries(counts).map(([stage, count]) => ({ stage, count })).sort((a, b) => b.count - a.count);
    invested = new Set((stages || []).filter((s: any) => s.stage === 'invested').map((s: any) => s.connection_id)).size;
  }

  // Talent, as manager firm.
  const { data: tconns } = await supabase.from('talent_connections')
    .select('id, candidate_id, manager_feedback, requested_at').eq('manager_firm_id', firmId);
  const interviews = (tconns || []).filter((t: any) => t.manager_feedback === 'interviewed').length;
  const hires = (tconns || []).filter((t: any) => t.manager_feedback === 'hired').length;
  const candidatesConnected = new Set((tconns || []).map((t: any) => t.candidate_id)).size;
  const { count: jobsPosted } = await inRange('created_at', supabase.from('talent_jobs').select('*', { count: 'exact', head: true }).eq('firm_id', firmId));

  // Service, as requester firm.
  const { data: sconns } = await supabase.from('service_connections')
    .select('id, provider_id, provider:service_providers(category)').eq('requester_firm_id', firmId);
  const serviceConnections = (sconns || []).length;
  const serviceCategories = Array.from(new Set((sconns || []).map((s: any) => (Array.isArray(s.provider) ? s.provider[0]?.category : s.provider?.category)).filter(Boolean)));

  // DDQ + data room.
  const { count: ddqSubmitted } = await inRange('updated_at', supabase.from('ddqs').select('*', { count: 'exact', head: true }).eq('firm_id', firmId).eq('status', 'submitted'));
  const { count: docsUploaded } = await inRange('uploaded_at', supabase.from('data_room_documents').select('*', { count: 'exact', head: true }).eq('firm_id', firmId).is('deleted_at', null));
  // Documents belonging to this firm that were accessed by others.
  const { data: firmDocs } = await supabase.from('data_room_documents').select('id').eq('firm_id', firmId);
  const docIds = (firmDocs || []).map((d: any) => d.id);
  let docsAccessedByAllocators = 0;
  let uniqueAllocatorsAccessed = 0;
  if (docIds.length) {
    const { data: accessRows } = await supabase.from('data_room_access_log')
      .select('accessed_by_firm, accessed_at').in('document_id', docIds)
      .gte('accessed_at', range.start.toISOString()).lte('accessed_at', range.end.toISOString());
    docsAccessedByAllocators = (accessRows || []).length;
    uniqueAllocatorsAccessed = new Set((accessRows || []).map((a: any) => a.accessed_by_firm).filter(Boolean)).size;
  }

  // Pulse, assignments/responses for this firm's members.
  let surveysAssigned = 0, surveysCompleted = 0, lastSurveyAt: string | null = null;
  if (memberIds.length) {
    const { count: assigned } = await supabase.from('pulse_assignments').select('*', { count: 'exact', head: true }).in('profile_id', memberIds);
    surveysAssigned = assigned || 0;
    const { data: resp } = await supabase.from('pulse_responses').select('completed_at').in('profile_id', memberIds).eq('is_complete', true).order('completed_at', { ascending: false });
    surveysCompleted = (resp || []).length;
    lastSurveyAt = (resp || [])[0]?.completed_at || null;
  }

  // Events.
  const { count: eventsRegistered } = await supabase.from('event_registrations').select('*', { count: 'exact', head: true }).eq('firm_id', firmId);
  const { count: eventMeetings } = await supabase.from('event_investor_schedule').select('*', { count: 'exact', head: true }).eq('firm_id', firmId);

  return {
    pipelineStages, invested, connectionsApproved,
    interviews, hires, jobsPosted: jobsPosted || 0, candidatesConnected,
    serviceCategories, serviceConnections,
    ddqSubmitted: ddqSubmitted || 0, docsUploaded: docsUploaded || 0, docsAccessedByAllocators, uniqueAllocatorsAccessed,
    surveysAssigned, surveysCompleted, lastSurveyAt,
    eventsRegistered: eventsRegistered || 0, eventMeetings: eventMeetings || 0,
  };
}

// ── Alerts ───────────────────────────────────────────────────────────────────
export async function loadAlertRules(): Promise<AlertRule[]> {
  const { data, error } = await supabase.from('intelligence_alerts').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data as AlertRule[]) || [];
}

export async function loadAlertTriggers(): Promise<AlertTrigger[]> {
  const { data, error } = await supabase.from('intelligence_alert_triggers').select('*').order('triggered_at', { ascending: false });
  if (error) throw error;
  return (data as AlertTrigger[]) || [];
}

// Evaluate all active alert rules, insert triggers for new matches, return count inserted.
export async function evaluateAlerts(firms: FirmLite[]): Promise<number> {
  const rules = (await loadAlertRules()).filter(r => r.is_active);
  const existing = await loadAlertTriggers();
  const seen = new Set(existing.map(t => `${t.alert_id}:${t.firm_id}:${t.message}`));
  const firmName = new Map(firms.map(f => [f.id, f.name]));
  const now = new Date();
  const toInsert: { alert_id: string; firm_id: string | null; message: string }[] = [];

  // Preload last-active per firm and connection counts for milestone/inactive checks.
  const { data: allA } = await supabase.from('activity_log').select('firm_id, created_at').not('firm_id', 'is', null).order('created_at', { ascending: false }).limit(8000);
  const lastActive = new Map<string, string>();
  (allA || []).forEach((r: any) => { if (!lastActive.has(r.firm_id)) lastActive.set(r.firm_id, r.created_at); });

  for (const rule of rules) {
    const targetFirms = rule.firm_id ? firms.filter(f => f.id === rule.firm_id) : firms;

    if (rule.alert_type === 'firm_inactive') {
      const days = rule.threshold_value || 30;
      for (const f of targetFirms) {
        const la = lastActive.get(f.id);
        const inactiveDays = la ? Math.floor((now.getTime() - new Date(la).getTime()) / 86400000) : null;
        if (inactiveDays === null || inactiveDays >= days) {
          const msg = `${f.name} has been inactive for ${inactiveDays === null ? 'the entire tracked period' : `${inactiveDays} days`} (threshold ${days}d).`;
          const key = `${rule.id}:${f.id}:${msg}`;
          if (!seen.has(key)) { toInsert.push({ alert_id: rule.id, firm_id: f.id, message: msg }); seen.add(key); }
        }
      }
    } else if (rule.alert_type === 'connection_milestone') {
      const target = rule.threshold_value || 10;
      const { data: conns } = await supabase.from('connections').select('allocator_firm_id, manager_firm_id');
      const cc = new Map<string, number>();
      (conns || []).forEach((c: any) => {
        if (c.allocator_firm_id) cc.set(c.allocator_firm_id, (cc.get(c.allocator_firm_id) || 0) + 1);
        if (c.manager_firm_id) cc.set(c.manager_firm_id, (cc.get(c.manager_firm_id) || 0) + 1);
      });
      for (const f of targetFirms) {
        const n = cc.get(f.id) || 0;
        if (n >= target) {
          const msg = `${f.name} has reached ${n} connections (milestone ${target}).`;
          const key = `${rule.id}:${f.id}:${msg}`;
          if (!seen.has(key)) { toInsert.push({ alert_id: rule.id, firm_id: f.id, message: msg }); seen.add(key); }
        }
      }
    } else if (rule.alert_type === 'first_investment') {
      const { data: invested } = await supabase.from('pipeline_stages')
        .select('connection_id, created_at, connection:connections(manager_firm_id, allocator_firm_id)').eq('stage', 'invested');
      for (const row of (invested || []) as any[]) {
        const conn = Array.isArray(row.connection) ? row.connection[0] : row.connection;
        if (!conn) continue;
        const fids = [conn.manager_firm_id, conn.allocator_firm_id].filter(Boolean);
        for (const fid of fids) {
          if (rule.firm_id && rule.firm_id !== fid) continue;
          const msg = `First platform investment recorded involving ${firmName.get(fid) || 'a firm'} (connection ${row.connection_id}).`;
          const key = `${rule.id}:${fid}:${msg}`;
          if (!seen.has(key)) { toInsert.push({ alert_id: rule.id, firm_id: fid, message: msg }); seen.add(key); }
        }
      }
    } else if (rule.alert_type === 'event_low_attendance') {
      const pct = rule.threshold_value || 50;
      const { data: events } = await supabase.from('events').select('id, name, status');
      for (const ev of (events || []) as any[]) {
        const { count: cap } = await supabase.from('event_manager_slots').select('capacity', { count: 'exact', head: true }).eq('event_id', ev.id);
        const { count: filled } = await supabase.from('event_investor_schedule').select('*', { count: 'exact', head: true }).eq('event_id', ev.id);
        // Approximate capacity as slots * 10 when slot capacities unavailable.
        const capacity = (cap || 0) * 10;
        if (capacity > 0) {
          const rate = Math.round(((filled || 0) / capacity) * 100);
          if (rate < pct) {
            const msg = `Event "${ev.name}" attendance at ${rate}% (below ${pct}% threshold).`;
            const key = `${rule.id}:${null}:${msg}`;
            if (!seen.has(key)) { toInsert.push({ alert_id: rule.id, firm_id: null, message: msg }); seen.add(key); }
          }
        }
      }
    }

    await supabase.from('intelligence_alerts').update({ last_triggered_at: now.toISOString() }).eq('id', rule.id);
  }

  if (toInsert.length) {
    const { error } = await supabase.from('intelligence_alert_triggers').insert(toInsert);
    if (error) throw error;
  }
  return toInsert.length;
}

// ── Report notes ─────────────────────────────────────────────────────────────
export async function loadReportNotes(firmId: string, start: string, end: string): Promise<ReportNotes | null> {
  const { data } = await supabase.from('intelligence_report_notes')
    .select('*').eq('firm_id', firmId).eq('period_start', start).eq('period_end', end).maybeSingle();
  return (data as ReportNotes) || null;
}

export async function saveReportNotes(notes: ReportNotes, userId: string | null): Promise<void> {
  const existing = await loadReportNotes(notes.firm_id, notes.period_start, notes.period_end);
  const payload = {
    firm_id: notes.firm_id, period_start: notes.period_start, period_end: notes.period_end,
    executive_summary: notes.executive_summary, altconnect_notes: notes.altconnect_notes,
    event_notes: notes.event_notes, talent_notes: notes.talent_notes,
    service_notes: notes.service_notes, ai_notes: notes.ai_notes,
    updated_at: new Date().toISOString(),
  };
  if (existing?.id) {
    await supabase.from('intelligence_report_notes').update(payload).eq('id', existing.id);
  } else {
    await supabase.from('intelligence_report_notes').insert({ ...payload, created_by: userId });
  }
}
